/**
 * Quick Size Picker
 *
 * Powers the inline size-picker UI on product grid cards for multi-variant
 * (e.g. sneaker) products. Replaces the prior behavior of auto-selecting the
 * largest variant.
 *
 * Markup contract (see snippets/product-grid-item.liquid):
 *   [data-quick-size-picker]
 *     [data-quick-size-toggle]
 *     [data-quick-size-panel]
 *       form
 *         input[data-variant-id]
 *         ul.quick-size-picker__sizes
 *           li > button[data-quick-size-choice][data-variant-id="..."]
 *         button[data-quick-size-submit]
 */
(function () {
  'use strict';

  var SELECTORS = {
    picker: '[data-quick-size-picker]',
    toggle: '[data-quick-size-toggle]',
    panel: '[data-quick-size-panel]',
    form: '.quick-size-picker__form',
    sizeChoice: '[data-quick-size-choice]',
    variantInput: '[data-variant-id]',
    submit: '[data-quick-size-submit]',
    submitLabel: '[data-quick-size-btn-label]'
  };
  var OPEN_CLASS = 'quick-size-picker--open';
  var ACTIVE_CLASS = 'quick-size-picker__size--active';

  function closest(el, selector) {
    while (el && el.nodeType === 1) {
      if (el.matches && el.matches(selector)) return el;
      el = el.parentNode;
    }
    return null;
  }

  function closeAllExcept(except) {
    document.querySelectorAll(SELECTORS.picker + '.' + OPEN_CLASS).forEach(function (p) {
      if (p !== except) {
        p.classList.remove(OPEN_CLASS);
        var toggle = p.querySelector(SELECTORS.toggle);
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
      }
    });
  }

  function togglePicker(picker) {
    var isOpen = picker.classList.contains(OPEN_CLASS);
    closeAllExcept(picker);
    if (isOpen) {
      picker.classList.remove(OPEN_CLASS);
    } else {
      picker.classList.add(OPEN_CLASS);
    }
    var toggle = picker.querySelector(SELECTORS.toggle);
    if (toggle) toggle.setAttribute('aria-expanded', picker.classList.contains(OPEN_CLASS) ? 'true' : 'false');
  }

  function pickSize(picker, choiceBtn) {
    var variantId = choiceBtn.getAttribute('data-variant-id');
    var sizeLabel = (choiceBtn.textContent || '').trim().split('\n')[0].trim();
    picker.querySelectorAll(SELECTORS.sizeChoice).forEach(function (b) {
      b.classList.remove(ACTIVE_CLASS);
      b.setAttribute('aria-pressed', 'false');
    });
    choiceBtn.classList.add(ACTIVE_CLASS);
    choiceBtn.setAttribute('aria-pressed', 'true');

    var input = picker.querySelector(SELECTORS.variantInput);
    if (input) input.value = variantId;

    var submit = picker.querySelector(SELECTORS.submit);
    if (submit) {
      submit.disabled = false;
      submit.removeAttribute('disabled');
      var label = submit.querySelector(SELECTORS.submitLabel);
      if (label) {
        label.textContent = 'Add size ' + sizeLabel;
      }
    }
  }

  function onDocumentClick(e) {
    var toggle = closest(e.target, SELECTORS.toggle);
    if (toggle) {
      var picker = closest(toggle, SELECTORS.picker);
      if (picker) {
        e.preventDefault();
        togglePicker(picker);
        return;
      }
    }

    var choice = closest(e.target, SELECTORS.sizeChoice);
    if (choice && !choice.hasAttribute('aria-disabled')) {
      var pickerFromChoice = closest(choice, SELECTORS.picker);
      if (pickerFromChoice) {
        pickSize(pickerFromChoice, choice);
        return;
      }
    }

    // Click-outside closes any open pickers
    if (!closest(e.target, SELECTORS.picker)) {
      closeAllExcept(null);
    }
  }

  function onKeyDown(e) {
    if (e.key === 'Escape' || e.keyCode === 27) {
      closeAllExcept(null);
    }
  }

  /**
   * Fallback submit handler that posts the form via fetch to Shopify's cart
   * API. The theme's own cart drawer listens for the `cart:build` or
   * `cart:updated` events — we dispatch them after a successful add. If the
   * theme already attaches its own listener via [data-add-to-cart], it takes
   * precedence and our handler is a no-op.
   */
  function onFormSubmit(e) {
    var form = e.target;
    if (!form.matches(SELECTORS.form)) return;

    // Defer to the theme's own handler if present.
    if (form.dataset.quickSizeHandled === 'true') return;

    e.preventDefault();
    form.dataset.quickSizeHandled = 'true';

    var formData = new FormData(form);
    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      body: formData
    })
      .then(function (res) { return res.json().then(function (j) { return { ok: res.ok, data: j }; }); })
      .then(function (result) {
        form.dataset.quickSizeHandled = 'false';
        if (!result.ok) {
          var errContainer = form.querySelector('[data-cart-errors-container]');
          if (errContainer) errContainer.textContent = (result.data && result.data.description) || 'Unable to add to cart.';
          return;
        }
        document.dispatchEvent(new CustomEvent('cart:build'));
        document.dispatchEvent(new CustomEvent('cart:updated'));
        // Try to open the cart drawer if one exists.
        var drawerToggle = document.querySelector('[data-cart-drawer-toggle]');
        if (drawerToggle) drawerToggle.click();
      })
      .catch(function () {
        form.dataset.quickSizeHandled = 'false';
      });
  }

  function init() {
    document.addEventListener('click', onDocumentClick);
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('submit', onFormSubmit, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
